/**
 * The one decision every "publicá tu auto" door makes.
 *
 * WHY THIS EXISTS. The wizard draft is persisted so a host never loses 17 live
 * photos to an app restart — but every create entry point called `reset()`
 * unconditionally, which overwrites the persisted state synchronously. The single
 * button a returning host taps to resume was the button that destroyed the draft:
 * no dialog, no undo, while the X inside the wizard had a confirmation for
 * exactly that act. Persistence shipped and was unreachable in practice.
 *
 * So the choice belongs to the host, once, in one place:
 *   - no draft            -> reset() and start clean (today's behaviour)
 *   - a draft exists      -> ask: continue it, or start over
 *   - continue            -> navigate WITHOUT touching the store
 *   - start over          -> discardDraft(), which also frees the captured files
 *
 * `discardDraft()` is deliberately the only path that deletes captures: a bare
 * `reset()` also runs on any sign-out, including the involuntary one an expired
 * refresh token causes at app start, and deleting there would wipe a host's work
 * while their phone sat in their pocket.
 */
import { useCallback, useState } from 'react'
import { useRouter } from 'expo-router'
import {
  useListingWizardStore,
  hasListingDraft,
  WIZARD_STEPS,
  type WizardStep,
} from '../stores/listing-wizard.store'

/** Route for a step, carrying vehicleId when the draft already owns a server row. */
export function wizardHrefForStep(step: number, vehicleId: string | null): string {
  const name = WIZARD_STEPS[Math.max(0, Math.min(step, WIZARD_STEPS.length - 1))] ?? 'details'
  const base = `/hosting/new-listing/${name}`
  return vehicleId ? `${base}?vehicleId=${encodeURIComponent(vehicleId)}` : base
}

export interface OpenListingWizard {
  /** True when there is work worth offering to resume. */
  hasDraft: boolean
  /** Where "Continuá tu borrador" goes. */
  resumeHref: string
  /** Resume without mutating the draft. */
  resume: () => void
  /** Throw the draft away — state AND captured files — then start clean. */
  startOver: () => void
  /** No draft: start clean immediately. With a draft, the caller must ask first. */
  startFresh: () => void
}

export function useOpenListingWizard(): OpenListingWizard {
  const router = useRouter()
  const draft = useListingWizardStore()
  // CREATE drafts only. hasListingDraft is true on vehicleId alone, and nothing
  // clears entryMode when an edit session exits — so an abandoned edit persisted
  // as a resumable "draft": "Agregar vehículo" offered to continue it, the resume
  // href carried ?vehicleId=X, and submitting ran updateVehicle over the host's
  // FIRST car. Adding a second car silently overwrote the first.
  //
  // An edit draft now falls through to startFresh() -> reset(), which is what the
  // layout's mount guard already intends, and reset() keeps the capture files so
  // nothing is deleted. The half-saved publish retry still resumes: that draft is
  // entryMode 'create' with a vehicleId.
  const hasDraft = draft.entryMode === 'create' && hasListingDraft(draft)

  // Resuming MUST carry vehicleId when the draft owns a server row: the wizard
  // layout resets an edit-mode draft that arrives without the param, which would
  // silently discard the very draft we are resuming.
  const resumeHref = wizardHrefForStep(draft.currentStep, draft.vehicleId)

  const resume = useCallback(() => {
    router.push(resumeHref as never)
  }, [router, resumeHref])

  const startOver = useCallback(() => {
    useListingWizardStore.getState().discardDraft()
    router.push('/hosting/new-listing/details' as never)
  }, [router])

  const startFresh = useCallback(() => {
    useListingWizardStore.getState().reset()
    router.push('/hosting/new-listing/details' as never)
  }, [router])

  return { hasDraft, resumeHref, resume, startOver, startFresh }
}

/**
 * The full "open the wizard" behaviour every entry point needs, so no door can
 * drift from the others (they already did — one screen got the resume dialog and
 * the rest kept a bare reset()).
 *
 * Usage:
 *   const gate = useListingWizardGate()
 *   <Pressable onPress={gate.open} .../>
 *   <ResumeDraftDialog {...gate.dialogProps} testIDPrefix="hosting" />
 */
export function useListingWizardGate(): {
  open: () => void
  dialogProps: {
    visible: boolean
    onResume: () => void
    onStartOver: () => void
    onDismiss: () => void
  }
} {
  const wizard = useOpenListingWizard()
  const [visible, setVisible] = useState(false)

  const open = useCallback(() => {
    // A draft exists → ask. None → start clean immediately, no dialog.
    if (wizard.hasDraft) {
      setVisible(true)
      return
    }
    wizard.startFresh()
  }, [wizard])

  return {
    open,
    dialogProps: {
      visible,
      onResume: () => {
        setVisible(false)
        wizard.resume()
      },
      onStartOver: () => {
        setVisible(false)
        wizard.startOver()
      },
      onDismiss: () => setVisible(false),
    },
  }
}

/**
 * Opening an EXISTING listing to edit it.
 *
 * Editing needs a clean store, so it resets — which silently destroyed a
 * persisted create draft on the most natural gesture on the dashboard: tapping
 * your own car. The whole card is the pressable and reads as "look at my
 * listing", while the header two screens back still promised "guardamos tu
 * avance automáticamente".
 *
 * Lives here, not copy-pasted per screen, because that copy-paste is exactly how
 * one door kept the guard and three others did not.
 */
export function useEditListingGate(): {
  /**
   * `step` existe para los avisos que ya saben QUÉ hay que arreglar. Mandar a
   * alguien al paso 1 para que renueve una tarjeta vencida son seis pantallas
   * de peaje entre el aviso y la acción, y cada una es una oportunidad de
   * abandonar. Por omisión abre el resumen editable, para elegir una sección
   * sin volver a recorrer el formulario de publicación.
   */
  openForEdit: (vehicleId: string, step?: WizardStep) => void
  dialogProps: {
    visible: boolean
    onKeepDraft: () => void
    onDiscardAndEdit: () => void
    onDismiss: () => void
  }
} {
  const router = useRouter()
  const [pendingEdit, setPendingEdit] = useState<{ id: string; step: WizardStep } | null>(null)

  const goToEdit = useCallback(
    (vehicleId: string, step: WizardStep = 'review') => {
      // ONE write. reset() then setVehicleId() is two, and persist flushes each
      // synchronously to MMKV — so the DISK briefly held
      // { entryMode: 'create', vehicleId: <a LIVE car> }. A kill in that window
      // made it durable, and the create-door resume gate would then hand that id
      // to a NEW listing and overwrite the published row: exactly the bug the
      // entryMode scoping was added to close, reopened by a two-step write.
      useListingWizardStore.getState().beginEdit(vehicleId)
      router.push(
        `/hosting/new-listing/${step}?vehicleId=${encodeURIComponent(vehicleId)}` as never,
      )
    },
    [router],
  )

  const openForEdit = useCallback(
    (vehicleId: string, step: WizardStep = 'review') => {
      const draft = useListingWizardStore.getState()
      if (draft.entryMode === 'create' && hasListingDraft(draft)) {
        setPendingEdit({ id: vehicleId, step })
        return
      }
      // Already editing THIS car with unsaved changes: resume, never restart.
      //
      // goToEdit calls beginEdit, which resets to INITIAL_STATE — so tapping
      // your own car a second time threw away everything typed since it loaded,
      // while the exit dialog had just promised "seguí después desde donde
      // ibas". The guard has to be HERE, ahead of beginEdit: by the time the
      // wizard layout mounts, the reset has already happened.
      //
      // dirtySinceLoad is false for a merely-opened edit (loadFromVehicle clears
      // it), so this cannot fire on the ordinary path; if it is somehow unset we
      // fall through to today's behaviour and reload from the server.
      if (draft.entryMode === 'edit' && draft.vehicleId === vehicleId && draft.dirtySinceLoad) {
        router.push(
          `/hosting/new-listing/${step}?vehicleId=${encodeURIComponent(vehicleId)}` as never,
        )
        return
      }
      goToEdit(vehicleId, step)
    },
    [goToEdit, router],
  )

  return {
    openForEdit,
    dialogProps: {
      visible: pendingEdit !== null,
      // Keep the draft = cancel the edit. Nothing is touched.
      onKeepDraft: () => setPendingEdit(null),
      onDiscardAndEdit: () => {
        const target = pendingEdit
        setPendingEdit(null)
        if (!target) return
        // discardDraft, never reset: the captures must be freed, not orphaned.
        useListingWizardStore.getState().discardDraft()
        goToEdit(target.id, target.step)
      },
      onDismiss: () => setPendingEdit(null),
    },
  }
}
